'use client';

import appShellStore from '@/lib/offline/appShellStore';
import { getApiUrl } from '@/lib/apiConfig';
import { analyzeReceiptImages } from '@/lib/receiptAiAnalysis';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

export interface PendingChildEntryPayload {
  parent_work_item_id: string | null;
  entry_type: string;
  amount?: number;
  notes?: string;
  metadata?: Record<string, unknown>;
  photos?: string[];
  location_lat?: number;
  location_lng?: number;
  location_accuracy?: number;
  vehicle_id?: string | null;
  vehicle_registration?: string | null;
}

export interface PendingChildEntry {
  id: string;
  payload: PendingChildEntryPayload;
  createdAt: string;
  retryCount: number;
  lastError?: string;
}

const CHILD_ENTRY_KEY = 'smarttask_pending_child_entries';
const MAX_CHILD_ENTRY_RETRIES = 5;

export async function loadPendingChildEntries(clientId: number): Promise<PendingChildEntry[]> {
  return appShellStore.loadSettingsArrayMigration<PendingChildEntry>(clientId, CHILD_ENTRY_KEY);
}

export async function savePendingChildEntries(clientId: number, entries: PendingChildEntry[]): Promise<void> {
  await appShellStore.saveSettingsArrayMigration(clientId, CHILD_ENTRY_KEY, entries);
}

export async function getPendingChildEntryCount(clientId: number): Promise<number> {
  if (!clientId) return 0;
  const entries = await loadPendingChildEntries(clientId);
  return entries.length;
}

async function enrichChildEntryPayload(payload: PendingChildEntryPayload, token: string): Promise<PendingChildEntryPayload> {
  const nextPayload: PendingChildEntryPayload = {
    ...payload,
    metadata: { ...(payload.metadata || {}) },
  };

  if (Array.isArray(nextPayload.photos) && nextPayload.photos.length > 0 && !nextPayload.metadata?.receipt_ai_validation) {
    try {
      const analyzed = await analyzeReceiptImages({
        images: nextPayload.photos,
        declared_amount: nextPayload.amount,
        entry_type: nextPayload.entry_type,
        accessToken: token,
      });

      if (analyzed.success) {
        const receiptData = analyzed.receipt;
        const validation = analyzed.validation;
        nextPayload.metadata = {
          ...nextPayload.metadata,
          receipt_ai_validation: {
            vendor_name: receiptData.vendor_name,
            receipt_number: receiptData.receipt_number,
            receipt_date: receiptData.receipt_date,
            detected_amount: receiptData.total_amount,
            subtotal: receiptData.subtotal ?? null,
            tax_amount: receiptData.tax_amount ?? null,
            currency: receiptData.currency ?? null,
            payment_method: receiptData.payment_method ?? null,
            line_items: Array.isArray(receiptData.line_items)
              ? receiptData.line_items.map((item) => ({
                  description: item.description,
                  quantity: item.quantity,
                  unit_price: item.unit_price,
                  total: item.total,
                }))
              : [],
            confidence: receiptData.confidence,
            validation_match: validation?.match || 'unknown',
            difference: validation?.difference ?? null,
          },
        };

        if (nextPayload.amount == null && receiptData.total_amount != null) {
          nextPayload.amount = Number(receiptData.total_amount);
        }
      }
    } catch (error) {
      if (!isSilentFetchError(error)) {
        console.warn('[SmartTaskOfflineEntries] Receipt AI replay failed, continuing without it:', error);
      }
    }
  }

  const odometerCapture = nextPayload.metadata?.odometer_capture as
    | {
        photo_data?: string;
        ai_validation?: string;
        reading?: number;
      }
    | undefined;
  if (odometerCapture?.photo_data && (!odometerCapture.ai_validation || odometerCapture.ai_validation === 'pending_offline')) {
    try {
      const odometerRes = await fetch(getApiUrl('/api/v1/work-items/fleet/verify-odometer'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          image: odometerCapture.photo_data,
          expected_reading: odometerCapture.reading,
          vehicle_name: nextPayload.vehicle_registration || undefined,
        }),
      });

      const odometerData = await odometerRes.json().catch(() => null);
      if (odometerRes.ok && odometerData?.success && odometerData.odometer) {
        nextPayload.metadata = {
          ...nextPayload.metadata,
          odometer_capture: {
            ...odometerCapture,
            ai_confidence: odometerData.odometer.confidence ?? null,
            ai_validation: odometerData.odometer.validation ?? null,
            detected_reading: odometerData.odometer.detected_reading ?? null,
            pending_verification: false,
          },
        };
      }
    } catch (error) {
      if (!isSilentFetchError(error)) {
        console.warn('[SmartTaskOfflineEntries] Odometer AI replay failed, continuing without it:', error);
      }
    }
  }

  return nextPayload;
}

export async function syncPendingChildEntries(params: {
  clientId: number;
  token: string;
  onProgress?: (progress: { current: number; total: number; message: string }) => void;
}): Promise<{ synced: number; failed: number; remaining: number }> {
  const { clientId, token, onProgress } = params;
  if (!clientId || !token) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  const queue = await loadPendingChildEntries(clientId);
  if (queue.length === 0) {
    return { synced: 0, failed: 0, remaining: 0 };
  }

  const remaining: PendingChildEntry[] = [];
  let synced = 0;
  let failed = 0;

  for (let index = 0; index < queue.length; index += 1) {
    const entry = queue[index];
    onProgress?.({
      current: index + 1,
      total: queue.length,
      message: `Syncing SmartTask child entry ${index + 1} of ${queue.length}`,
    });

    try {
      const payload = await enrichChildEntryPayload(entry.payload, token);
      const res = await fetch(getApiUrl('/api/v1/work-items/child-entries'), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => null);
        const message = body?.error || 'Failed to sync child entry';
        if (res.status >= 400 && res.status < 500) {
          throw new Error(`NON_RETRYABLE:${message}`);
        }
        throw new Error(message);
      }

      synced += 1;
    } catch (error: unknown) {
      failed += 1;
      const errMsg = error instanceof Error ? error.message : String(error);
      const nonRetryable = typeof errMsg === 'string' && errMsg.startsWith('NON_RETRYABLE:');
      const nextRetryCount = entry.retryCount + 1;
      if (!nonRetryable && nextRetryCount < MAX_CHILD_ENTRY_RETRIES) {
        remaining.push({
          ...entry,
          retryCount: nextRetryCount,
          lastError: errMsg || 'Sync failed',
        });
      } else {
        if (!isSilentFetchError(error)) {
          console.error('[SmartTaskOfflineEntries] Dropping failed child entry:', errMsg || error);
        }
      }
    }
  }

  await savePendingChildEntries(clientId, remaining);
  return { synced, failed, remaining: remaining.length };
}